// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { useEffect, useState } from 'react';
import styles from "./styles";
import { Box, Button, Divider, Tooltip, Typography, makeStyles } from '@material-ui/core';
import { getI18n } from '../../../utils/intl';
import { useIntl } from 'react-intl';
import cautionIcon from '../../../icons/caution.svg';
import subtractIcon from '../../../icons/subtract.svg';
import crossIcon from '../../../icons/crossIcon.svg';
import tickIcon from '../../../icons/tickIcon.svg';
import { useDispatch, useSelector } from 'react-redux';
import { getFeatureFlagApiPlans } from '../../../reducers/portalConfig';
import { enableApiPlans } from '../../../actions/apiPlans';
import { getApiPlansLoading, getEnableApiPlansData, getApiPlansValidationError, getApiPlansError } from '../../../reducers/apiPlans';
import { AlertMessages, FormDialog, Loading } from '../../../components';
import clsx from 'clsx';
import { get } from 'lodash';
import { ROUTES } from '../../../constants/routes';

const getAlertDialogTitle = (intl, classes) => {
  return (
    <div className={classes.flexNonBetween}>
      <img
        src={subtractIcon}
      />
      <Typography className={classes.alertDialogTitle} variant='h5'>{intl?.getI18nMessage("label.api.plans.enable.form.dialog.title")}</Typography>
    </div>
  )
}

const EnableApiPlans = () => {

  const [apiGroupsActiveMessage, setApiGroupsActiveMessage] = useState('');
  const [noEnrolledProxiesMessage, setNoEnrolledProxiesMessage] = useState('');
  const [inValidGateWayVersionMessage, setInValidGateWayVersionMessage] = useState('');
  const [inValidEnrollmentBundleMessage, setInValidEnrollmentBundleMessage] = useState('');
  const [inValidOtkVersionMessage, setInValidOtkVersionMessage] = useState('');
  const [unKnownOtkVersionMessage, setUnKnownOtkVersionMessage] = useState('');
  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [alertMessages, setAlertMessages] = useState({
    message: "",
    status: false,
    variant: "",
  })

  const useStyles = makeStyles(styles);
  const classes = useStyles();
  const intl = getI18n(useIntl());

  const isLoading = useSelector(getApiPlansLoading);
  const enableApiPlansData = useSelector(getEnableApiPlansData);
  const enableApiPlansValidationError = useSelector(getApiPlansValidationError);
  const apiPlansError = useSelector(getApiPlansError);
  const isApiPlansEnabled = useSelector(getFeatureFlagApiPlans);
  const invalidTenantError = enableApiPlansValidationError &&
    enableApiPlansValidationError[0]?.field === "INVALID_INGRESS_TENANT" &&
    enableApiPlansValidationError[0]?.error;

  const dispatch = useDispatch();

  const closeNotification = () => {
    setAlertMessages({
      ...alertMessages,
      status: false,
    })
  }
  const refreshErrorMessages = () => {
    setApiGroupsActiveMessage("");
    setNoEnrolledProxiesMessage("");
    setInValidEnrollmentBundleMessage("");
    setInValidGateWayVersionMessage("");
    setInValidOtkVersionMessage("");
    setUnKnownOtkVersionMessage("");
  }

  const handleEnableApiPlans = () => {
    closeNotification();
    refreshErrorMessages();
    const payload = {
      "name": "FEATURE_FLAG_API_PLANS",
      "value": "true",
      "uuid": "00000000-0000-0000-0000-000000000000",
    }
    dispatch(enableApiPlans(payload));
  }

  function getErrorMessages(validationErrors) {
    for (const error of validationErrors) {
      const errorField = get(error, 'field', '')
      if (errorField === "API_GROUPS_IN_USE" && error.key === "error.apiPlan.validation.flag.invalid.apiGroups") {
        setApiGroupsActiveMessage(intl.getI18nMessage("label.api.plans.enable.api.groups.active.message"));
      } else if (errorField.startsWith("NO_ENROLLED_PROXIES") && error.key === "error.apiPlan.validation.proxy.notfound") {
        setNoEnrolledProxiesMessage(intl.getI18nMessage("label.api.plans.enable.no.enrolled.proxeis.message"));
      } else if (errorField.startsWith("INVALID_GATEWAY_VERSION")) {
        setInValidGateWayVersionMessage(intl.getI18nMessage("label.api.plans.enable.invalid.gateway.version.message"));
      } else if (errorField.startsWith("INVALID_ENROLLMENT_BUNDLE")) {
        setInValidEnrollmentBundleMessage(intl.getI18nMessage("label.api.plans.enable.invalid.enrollment.bundle.message"));
      } else if (errorField.startsWith("INVALID_OTK_VERSION")) {
        setInValidOtkVersionMessage(intl.getI18nMessage("label.api.plans.enable.invalid.otk.version.message"));
      } else if (errorField.startsWith("UNKNOWN_OTK_VERSION")) {
        setUnKnownOtkVersionMessage(intl.getI18nMessage("label.api.plans.enable.unknown.otk.version.message"));
      }
    }
  }

  useEffect(() => {
    if (invalidTenantError) {
      setAlertMessages({
        status: true,
        message: invalidTenantError,
        variant: "error",
      })
    }
  }, [invalidTenantError])

  useEffect(() => {
    if ((enableApiPlansValidationError && !invalidTenantError) && !apiPlansError) {
      setOpenFormDialog(true);
      getErrorMessages(enableApiPlansValidationError);
    } else if (enableApiPlansData !== null) {
      setOpenFormDialog(false);
      window.location.href = ROUTES.apiPlans.path;
    }
  }, [enableApiPlansValidationError, enableApiPlansData, apiPlansError, invalidTenantError]);

  useEffect(() => {
    if (isApiPlansEnabled) {
      window.location.href = ROUTES.apiPlans.path;
    }
  }, [isApiPlansEnabled])

  const formDialogTheadClass = clsx(classes.formDialogCell, classes.formDialogHead);

  const invalidGatewayVersion = inValidGateWayVersionMessage || noEnrolledProxiesMessage;
  const invalidEnrollmentBundle = inValidEnrollmentBundleMessage || noEnrolledProxiesMessage;
  const invalidOTKVersion = unKnownOtkVersionMessage || inValidOtkVersionMessage ||
    noEnrolledProxiesMessage;

  const formDialogCell = clsx(classes.formDialogCell, classes.formDialogCellContent);
  const actionFormDialogCell = clsx(classes.formDialogCell, classes.actionFormDialogCell);

  return (
    <>
      <div className={classes.content}>
        {
          isLoading &&
          <Loading pageLoader />
        }
        {
          (alertMessages.status) && (
            <AlertMessages
              className={classes.alertContainer}
              variant={alertMessages.variant}
              message={alertMessages.message}
              onClose={() => closeNotification()}
              id="api-plans-enable-snack-bar"
              data-apim-test="api-plans-enable-snack-bar"
            />
          )
        }
        <Typography variant='h5'>{intl.getI18nMessage("label.api.plans")}</Typography>
        <div className={classes.flexNonBetween}>
          <Typography variant='h2'>{intl.getI18nMessage("label.api.plans.enable.title")}</Typography>
          <Tooltip
            title={intl.getI18nMessage("label.api.plans.enable.tool.tip.message")}
            arrow
            placement='right'

          >
            <img src={cautionIcon}
              className={classes.cautionIcon}
            />
          </Tooltip>
        </div>
        <div className={classes.flexNonAlign}>
          <Box>
            <Typography variant='h4'>{intl.getI18nMessage("label.api.plans.enable.why.text")}</Typography>
            <ol className={classes.orderedList}>
              <li><Typography>{intl.getI18nMessage("label.api.plans.enable.why.flexible.info")}</Typography></li>
              <li><Typography>{intl.getI18nMessage("label.api.plans.enable.why.rate.limit.quotos.info")}</Typography></li>
              <li><Typography>{intl.getI18nMessage("label.api.plans.enable.why.organizations.info")}</Typography></li>
            </ol>
          </Box>

          <Box>
            <Typography variant='h4'>{intl.getI18nMessage("label.api.plans.enable.requirements.title")}</Typography>
            <div>
              <Typography variant='h5' className={classes.requirementsTitle}>{intl.getI18nMessage("label.api.plans.enable.minimum.requirements.title")}</Typography>
              <ul className={classes.list}>
                <li><Typography>{intl.getI18nMessage("label.api.plans.enable.minimum.requirements.description")}</Typography></li>
              </ul>
            </div>
            <div>
              <Typography variant='h5' className={classes.requirementsTitle}>{intl.getI18nMessage("label.api.plans.enable.please.note.title")}</Typography>
              <ul className={classes.list}>
                <li><Typography>{intl.getI18nMessage("label.api.plans.enable.please.note.one.time.change.description")}</Typography></li>
                <li><Typography>{intl.getI18nMessage("label.api.plans.enable.please.note.enroll.description")}</Typography></li>
              </ul>
            </div>

            <Button
              variant='contained'
              color='secondary'
              data-apim-test='enable-api-plans'
              onClick={handleEnableApiPlans}
            >
              {intl.getI18nMessage("label.api.plans.enable.title")}
            </Button>
          </Box>
        </div>

        <FormDialog
          paperClass={classes.formDialogContainer}
          isOpen={openFormDialog}
          onSubmit={handleEnableApiPlans}
          onCancel={() => setOpenFormDialog(false)}
          title={getAlertDialogTitle(intl, classes)}
          cancelText={intl.getI18nMessage("label.close.button")}
          submitText={intl.getI18nMessage("label.api.plans.enable.form.dialog.refresh.button.text")}
        >
          <Typography className={classes.alertDialogDescription}>{intl.getI18nMessage("label.api.plans.enable.form.dialog.description")}</Typography>
          <div className={classes.flex}>
            <Typography className={formDialogTheadClass}>{intl.getI18nMessage("label.api.plans.enable.requirements.title")}</Typography>
            <Typography className={formDialogTheadClass}>{intl.getI18nMessage("label.status")}</Typography>
            <Typography className={formDialogTheadClass}>{intl.getI18nMessage("label.api.plans.enable.form.dialog.required.actions.message")}</Typography>
          </div>

          <Divider />

          <div className={classes.flex}>
            <Typography className={formDialogCell}>{intl.getI18nMessage("label.api.plans.enable.form.dialog.requirements.gateway.text")}</Typography>
            <div className={formDialogCell}>
              {
                invalidGatewayVersion ?
                  <div className={classes.flexNonBetween}>
                    <img src={crossIcon} className={classes.formDialogIcon} />
                    {invalidGatewayVersion}
                  </div> :
                  <div className={classes.flexNonBetween}>
                    <img src={tickIcon} className={classes.formDialogIcon} />
                    {intl.getI18nMessage("label.api.plans.enable.form.dialog.gateway.version.matched.text")}
                  </div>
              }
            </div>
            <div className={actionFormDialogCell}>
              {
                invalidGatewayVersion ?
                  <div className={classes.flexNonBetween}>
                    {intl.getI18nMessage("label.api.plans.enable.form.dialog.enroll.proxies.text")}
                    <Button
                      variant="text"
                      color="secondary"
                      onClick={() => window.open('/admin/api-proxy', '_blank')}
                    >
                      {intl.getI18nMessage("label.api.plans.enable.form.dialog.view.proxies.button.text")}
                    </Button>
                  </div> :
                  <Typography className={classes.formDialogNone}>{intl.getI18nMessage("label.none")}</Typography>
              }
            </div>
          </div>

          <Divider />

          <div className={classes.flex}>
            <Typography className={formDialogCell}>{intl.getI18nMessage("label.api.plans.enable.form.dialog.requirements.enrollment.text")}</Typography>
            <div className={formDialogCell}>
              {
                invalidEnrollmentBundle ?
                  <div className={classes.flexNonBetween}>
                    <img src={crossIcon} className={classes.formDialogIcon} />
                    {invalidEnrollmentBundle}
                  </div> :
                  <div className={classes.flexNonBetween}>
                    <img src={tickIcon} className={classes.formDialogIcon} />
                    {intl.getI18nMessage("label.proxy.details.page.overview.portal.compatible")}
                  </div>
              }
            </div>
            <div className={actionFormDialogCell}>
              {
                invalidEnrollmentBundle ?
                  <div className={classes.flexNonBetween}>
                    {intl.getI18nMessage("label.api.plans.enable.form.dialog.enroll.proxies.text")}
                    <Button
                      variant="text"
                      color="secondary"
                      onClick={() => window.open('/admin/api-proxy', '_blank')}
                    >
                      {intl.getI18nMessage("label.api.plans.enable.form.dialog.view.proxies.button.text")}
                    </Button>
                  </div> :
                  <Typography className={classes.formDialogNone}>{intl.getI18nMessage("label.none")}</Typography>
              }
            </div>
          </div>

          <Divider />

          <div className={classes.flex}>
            <Typography className={formDialogCell}>{intl.getI18nMessage("label.api.plans.enable.form.dialog.requirements.otk.text")}</Typography>
            <div className={formDialogCell}>
              {
                invalidOTKVersion ?
                  <div className={classes.flexNonBetween}>
                    <img src={crossIcon} className={classes.formDialogIcon} />
                    {invalidOTKVersion}
                  </div> :
                  <div className={classes.flexNonBetween}>
                    <img src={tickIcon} className={classes.formDialogIcon} />
                    {intl.getI18nMessage("label.api.plans.enable.form.dialog.otk.version.matched.text")}
                  </div>
              }
            </div>
            <div className={actionFormDialogCell}>
              {
                invalidOTKVersion ?
                  <div className={classes.flexNonBetween}>
                    {intl.getI18nMessage("label.api.plans.enable.form.dialog.enroll.proxies.text")}
                    <Button
                      variant="text"
                      color="secondary"
                      onClick={() => window.open('/admin/api-proxy', '_blank')}
                    >
                      {intl.getI18nMessage("label.api.plans.enable.form.dialog.view.proxies.button.text")}
                    </Button>
                  </div> :
                  <Typography className={classes.formDialogNone}>{intl.getI18nMessage("label.none")}</Typography>
              }
            </div>
          </div>

          <Divider />

          <div className={classes.flex}>
            <Typography className={formDialogCell}>{intl.getI18nMessage("label.api.plans.enable.form.dialog.requirements.api.group.text")}</Typography>
            <div className={formDialogCell}>
              {
                apiGroupsActiveMessage ?
                  <div className={classes.flexNonBetween}>
                    <img src={crossIcon} className={classes.formDialogIcon} />
                    {apiGroupsActiveMessage}
                  </div> :
                  <div className={classes.flexNonBetween}>
                    <img src={tickIcon} className={classes.formDialogIcon} />
                    {intl.getI18nMessage("label.api.plans.enable.form.dialog.no.api.groups.active.text")}
                  </div>
              }
            </div>
            <div className={actionFormDialogCell}>
              {
                apiGroupsActiveMessage ?
                  <div className={classes.flexNonBetween}>
                    {intl.getI18nMessage("label.api.plans.enable.form.dialog.delete.api.groups.text")}
                    <Button
                      variant="text"
                      color="secondary"
                      onClick={() => window.open('/admin/api-groups', '_blank')}
                    >
                      {intl.getI18nMessage("label.api.plans.enable.form.dialog.view.api.groups.button.text")}
                    </Button>
                  </div> :
                  <Typography className={classes.formDialogNone}>{intl.getI18nMessage("label.none")}</Typography>
              }
            </div>
          </div>

          <Divider />

        </FormDialog>
      </div>
    </>
  )
}

export default EnableApiPlans;
